// Cart drawer: opens from the cart icon, catches add to cart, changes the cart through the Ajax Cart API and
// re-renders itself with the Section Rendering API. Free gifts and preselected add-ons are kept in sync here.
// Works with Shopify apps: any cart change made by an app (fetch or XHR to /cart/add, change, update, clear) refreshes
// the drawer and the header count; apps can also call window.theme.cart.open() / .refresh() or send a
// 'cart:refresh' event. Theme requests carry the X-Theme-Cart header so they are not counted twice.
(() => {
  if (customElements.get('cart-drawer')) return;
  const mobile = window.matchMedia('(max-width: 749px)');
  const json = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Theme-Cart': '1' };
  const store = {
    get: (key) => { try { return localStorage.getItem(key); } catch (error) { return null; } },
    set: (key, value) => { try { value == null ? localStorage.removeItem(key) : localStorage.setItem(key, value); } catch (error) { /* private mode */ } },
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('scroll', (event) => this.onScroll(event), true);
    }

    get active() {
      return document.body.dataset.cartType === 'drawer' && this.dataset[mobile.matches ? 'showMobile' : 'showDesktop'] === 'true';
    }

    get content() {
      return this.querySelector('[data-cart-content]');
    }

    // ---------- Open and close ----------
    open() {
      if (this.classList.contains('is-open') || this.opening) return;
      // the drawer CSS loads without holding up the page (sections/cart-drawer.liquid): open once it has arrived
      const sheet = document.querySelector('link[data-cart-drawer-css]');
      if (sheet && sheet.media === 'print') {
        const retry = () => { sheet.media = 'all'; this.open(); };
        sheet.addEventListener('load', retry, { once: true });
        sheet.addEventListener('error', retry, { once: true });
        return;
      }
      if (!this.synced) { this.synced = true; this.syncAutomatic(); }
      this.returnFocus = document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('cart-drawer-lock');
      // Two frames so the slide-in transition runs; close() before then cancels it (quick open/close or double click).
      this.opening = true;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.opening) return;
        this.opening = false;
        this.classList.add('is-open');
        this.querySelector('.cart-drawer__panel').focus({ preventScroll: true });
      }));
    }

    close() {
      if (!this.classList.contains('is-open') && !this.opening) return;
      this.opening = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('cart-drawer-lock');
      const panel = this.querySelector('.cart-drawer__panel');
      const done = () => { if (!this.classList.contains('is-open')) this.hidden = true; };
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
      this.returnFocus?.focus?.({ preventScroll: true });
    }

    // ---------- Cart requests ----------
    post(path, body) {
      return cartPost(path, body);
    }

    // Cart requests ask Shopify to render the drawer in the same response (Section Rendering in the Ajax Cart API),
    // so the drawer updates after one round trip instead of two.
    get sectionParams() {
      return this.active ? { sections: this.dataset.sectionId, sections_url: window.location.pathname } : null;
    }

    take(data) {
      const html = data?.sections?.[this.dataset.sectionId];
      if (html) this.nextHtml = html;
    }

    add(items) {
      return this.post('cart/add.js', { items });
    }

    change(line, quantity) {
      return this.post('cart/change.js', { id: line, quantity });
    }

    // Runs one cart change, then re-renders the drawer. Changes wait for each other.
    run(task) {
      this.queue = (this.queue || Promise.resolve()).then(async () => {
        this.content?.classList.add('is-busy');
        try {
          await task();
        } catch (error) {
          this.toast(error.message);
        }
        await this.refresh(true);
        await this.syncAutomatic();
        this.content?.classList.remove('is-busy');
      });
      return this.queue;
    }

    // A change made outside the theme (app, event): always fetch the drawer fresh.
    reload() {
      this.nextHtml = null;
      return this.run(() => Promise.resolve());
    }

    // taken = use the drawer HTML that came with the last cart request (if any); otherwise fetch it.
    async refresh(taken = false) {
      let html = taken ? this.nextHtml : null;
      this.nextHtml = null;
      if (!html) {
        const url = `${this.dataset.rootUrl || '/'}?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
        html = await fetch(url, { headers: { Accept: 'text/html' } }).then((response) => response.text()).catch(() => '');
      }
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
      if (!next) return;
      const body = this.querySelector('.cart-drawer__body');
      const scroll = body ? body.scrollTop : 0;
      this.content.replaceWith(next);
      const newBody = this.querySelector('.cart-drawer__body');
      if (newBody) newBody.scrollTop = scroll;
      const count = Number(next.dataset.itemCount) || 0;
      this.lastRefresh = Date.now();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: count } }));
    }

    // Free gift: added when unlocked (if set to), removed when no longer unlocked.
    // Preselected add-ons: added while the cart has the shopper's own items, unless they switched it off.
    // Gifts added with a product (Quantity breaks, subscriptions): removed with it (see giftsToRemove).
    async syncAutomatic() {
      if (this.syncing) return;
      this.syncing = true;
      let changed = false;
      try {
        // Gifts that came with a product and Quantity breaks tier codes, for what is in the cart now (only when the
        // cart has such gifts or Quantity breaks has used tier codes on this device: no extra request otherwise).
        if ((this.querySelector('[data-gift-for]') || store.get(tierKey) || store.get(giftKey)) && await syncCart()) changed = true;
        const ownCount = Number(this.content?.dataset.ownCount) || 0;
        const gift = this.querySelector('[data-gift]');
        if (gift) {
          const unlocked = gift.dataset.unlocked === 'true';
          const dismissed = store.get(`cart-gift-off:${gift.dataset.variantId}`) === '1';
          if (!unlocked && gift.dataset.lineKey) { await this.change(gift.dataset.lineKey, 0); changed = true; }
          else if (unlocked && gift.dataset.autoAdd === 'true' && !gift.dataset.lineKey && !dismissed) {
            await this.add([{ id: Number(gift.dataset.variantId), quantity: 1, properties: { _free_gift: '1' } }]);
            changed = true;
          }
        }
        for (const addon of this.querySelectorAll('[data-addon]')) {
          const off = store.get(`cart-addon-off:${addon.dataset.variantId}`) === '1';
          if (ownCount === 0 && addon.dataset.lineKey) { await this.change(addon.dataset.lineKey, 0); changed = true; }
          else if (ownCount > 0 && addon.dataset.defaultOn === 'true' && !off && !addon.dataset.lineKey) {
            await this.add([{ id: Number(addon.dataset.variantId), quantity: 1, properties: { _addon: '1' } }]);
            changed = true;
          }
        }
      } catch (error) {
        this.toast(error.message);
      }
      if (changed) await this.refresh(true);
      this.syncing = false;
    }

    // ---------- Events inside the drawer ----------
    onClick(event) {
      const target = event.target.closest('button, a, [data-cart-close]');
      if (!target) return;

      if (target.matches('[data-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const line = target.closest('[data-line-key]');
      const card = target.closest('[data-card]');

      if (target.matches('[data-qty-step]')) {
        const input = target.parentElement.querySelector('input');
        const next = Math.max(line ? 0 : 1, (Number(input.value) || 0) + Number(target.dataset.qtyStep));
        input.value = next;
        if (line && !card) this.run(() => this.change(line.dataset.lineKey, next));
        else if (card) this.cardQuantity(card, next);
        return;
      }

      if (target.matches('[data-line-remove]') && line) {
        if (line.hasAttribute('data-free-gift')) store.set(`cart-gift-off:${line.dataset.variantId}`, '1');
        if (line.hasAttribute('data-addon-line')) store.set(`cart-addon-off:${line.dataset.variantId}`, '1');
        this.run(() => this.change(line.dataset.lineKey, 0));
        return;
      }

      if (target.matches('[data-card-add]') && card) {
        const quantity = Number(card.querySelector('[data-card-quantity]')?.value) || 1;
        target.setAttribute('aria-busy', 'true');
        this.run(() => this.add([{ id: Number(card.dataset.variantId), quantity }]));
        return;
      }

      if (target.matches('[data-card-toggle]') && card) {
        const on = target.getAttribute('aria-checked') === 'true';
        target.setAttribute('aria-checked', String(!on));
        const quantity = Number(card.querySelector('[data-card-quantity]')?.value) || 1;
        this.run(() => (on ? this.post('cart/change.js', { id: String(card.dataset.variantId), quantity: 0 }) : this.add([{ id: Number(card.dataset.variantId), quantity }])));
        return;
      }

      if (target.matches('[data-addon-toggle]')) {
        const addon = target.closest('[data-addon]');
        const key = `cart-addon-off:${addon.dataset.variantId}`;
        if (addon.dataset.lineKey) {
          target.setAttribute('aria-checked', 'false');
          store.set(key, '1');
          this.run(() => this.change(addon.dataset.lineKey, 0));
        } else {
          target.setAttribute('aria-checked', 'true');
          store.set(key, null);
          this.run(() => this.add([{ id: Number(addon.dataset.variantId), quantity: 1, properties: { _addon: '1' } }]));
        }
        return;
      }

      if (target.matches('[data-gift-add]')) {
        const gift = target.closest('[data-gift]');
        const id = Number(gift.querySelector('[data-variant-select]')?.value || gift.dataset.variantId);
        store.set(`cart-gift-off:${gift.dataset.variantId}`, null);
        this.run(() => this.add([{ id, quantity: 1, properties: { _free_gift: '1' } }]));
        return;
      }

      if (target.matches('[data-discount-apply]')) {
        const box = target.closest('[data-discount]');
        const code = box.querySelector('[data-discount-input]').value.trim();
        if (code) this.applyDiscount([...this.discountCodes(box), code], code);
        return;
      }

      if (target.matches('[data-discount-remove]')) {
        const box = target.closest('[data-discount]');
        this.applyDiscount(this.discountCodes(box).filter((c) => c !== target.dataset.discountRemove));
        return;
      }

      if (target.matches('[data-carousel-step]')) {
        const track = target.closest('.cd-upsells').querySelector('[data-carousel-track]');
        track.scrollBy({ left: track.clientWidth * Number(target.dataset.carouselStep), behavior: 'smooth' });
        return;
      }

      if (target.closest('[data-carousel-dots]')) {
        const dots = [...target.parentElement.children];
        const track = target.closest('.cd-upsells').querySelector('[data-carousel-track]');
        track.scrollTo({ left: track.clientWidth * dots.indexOf(target), behavior: 'smooth' });
      }
    }

    // A recommendation whose switch is on is in the cart: its quantity follows the card's quantity selector.
    cardQuantity(card, quantity) {
      if (card.querySelector('[data-card-toggle]')?.getAttribute('aria-checked') !== 'true') return;
      clearTimeout(card.quantityTimer);
      card.quantityTimer = setTimeout(() => this.run(() => this.post('cart/change.js', { id: String(card.dataset.variantId), quantity })), 300);
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-card-quantity]')) {
        const card = input.closest('[data-card]');
        input.value = Math.max(1, Number(input.value) || 1);
        this.cardQuantity(card, Number(input.value));
      }
      if (input.matches('[data-line-quantity]')) {
        const line = input.closest('[data-line-key]');
        this.run(() => this.change(line.dataset.lineKey, Math.max(0, Number(input.value) || 0)));
      }
      if (input.matches('[data-variant-select]')) {
        const card = input.closest('[data-card]');
        const option = input.selectedOptions[0];
        if (card) {
          card.dataset.variantId = input.value;
          card.querySelector('[data-card-price]').textContent = option.dataset.price;
          const compare = card.querySelector('[data-card-compare]');
          compare.textContent = option.dataset.compare || '';
          compare.hidden = !option.dataset.compare;
        }
      }
    }

    onInput(event) {
      if (!event.target.matches('[data-cart-note]')) return;
      clearTimeout(this.noteTimer);
      const note = event.target.value;
      this.noteTimer = setTimeout(() => this.post('cart/update.js', { note }).catch((error) => this.toast(error.message)), 500);
    }

    onKeydown(event) {
      if (event.key === 'Escape') { this.close(); return; }
      if (event.key === 'Enter' && event.target.matches('[data-discount-input]')) {
        event.preventDefault();
        event.target.closest('[data-discount]').querySelector('[data-discount-apply]').click();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.querySelectorAll('a[href], button:not([disabled]), input, select, textarea')].filter((el) => el.offsetParent);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    onScroll(event) {
      const track = event.target;
      if (!track.matches?.('[data-carousel-track]')) return;
      const dots = track.closest('.cd-upsells').querySelectorAll('[data-carousel-dots] button');
      const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }

    discountCodes(box) {
      return [...box.querySelectorAll('[data-discount-remove]')].map((button) => button.dataset.discountRemove);
    }

    applyDiscount(codes, newCode) {
      this.run(async () => {
        const cart = await this.post('cart/update.js', { discount: codes.join(',') });
        const applied = (cart.discount_codes || []).find((entry) => entry.code.toLowerCase() === (newCode || '').toLowerCase());
        if (newCode && applied && !applied.applicable) throw new Error(`${newCode}: this code cannot be used on your cart.`);
      });
    }

    toast(message) {
      const toast = this.querySelector('[data-cart-toast]');
      if (!toast || !message) return;
      toast.textContent = message;
      toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { toast.hidden = true; }, 3500);
    }

    get errorText() {
      return this.dataset.errorText || 'Something went wrong. Please try again.';
    }
  }

  customElements.define('cart-drawer', CartDrawer);
  const drawer = () => document.querySelector('cart-drawer');

  // The one cart request for the theme's scripts (window.theme.cart.post): JSON in, JSON out, errors thrown with
  // Shopify's message. When the drawer is on, the response also carries the freshly rendered drawer.
  async function cartPost(path, body) {
    const cartDrawer = drawer();
    const extra = cartDrawer?.sectionParams;
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, { method: 'POST', headers: json, body: JSON.stringify(extra ? { ...body, ...extra } : body) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || cartDrawer?.errorText || 'Something went wrong. Please try again.');
    cartDrawer?.take?.(data);
    return data;
  }

  // Keeps the cart in line with what Quantity breaks and subscriptions promised, after any cart change (drawer, cart
  // page, adding). One request at most. Used as window.theme.cart.sync; returns true when it changed the cart.
  // - Gifts added with a product (_gift + _for, assets/quantity-breaks.js) leave when that product left the cart, when
  //   fewer of it remain than the gift needs (_unlock: its tier or unlock quantity), or when its subscription left (_sub).
  // - Tier codes: main lines added by Quantity breaks carry its tier table (_tiers = "2:CODE2,3:CODE3"). For each
  //   product the cart keeps the code of the best tier its quantity reaches (lines with a table count), so lowering
  //   3 to 1 takes the 3+ code off and going back up puts it back; a tier code nothing qualifies for any more leaves.
  // - Tier gifts: main lines added by Quantity breaks carry every tier's gifts and the progressive gifts (_gifts, JSON);
  //   their gift lines are marked _tg. When the quantity of the product reaches another tier (or unlocks another
  //   progressive gift), those gift lines are replaced by the gifts of what it reaches now. A gift the shopper removed
  //   stays removed until the quantity reaches another tier.
  // add: other codes to apply (gifts). tierCodes: the codes Quantity breaks uses as tier codes (remembered, so they can
  // leave even when no line carries a table any more).
  const tierKey = 'theme:tier-codes';
  const giftKey = 'theme:tier-gifts';
  async function syncCart({ add = [], tierCodes = [] } = {}) {
    let known = [];
    try { known = JSON.parse(store.get(tierKey) || '[]'); } catch (error) { known = []; }
    if (tierCodes.length) {
      known = [...new Set([...known, ...tierCodes])].slice(-50);
      store.set(tierKey, JSON.stringify(known));
    }
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
    const lines = cart.items || [];
    if (!known.length && !add.length && !lines.some((line) => line.properties?._for || line.properties?._gifts)) return false;

    // gifts
    const owned = new Map();
    const subscribed = new Set();
    lines.forEach((line) => {
      if (line.properties?._gift || line.properties?._free_gift || line.properties?._addon) return;
      owned.set(String(line.product_id), (owned.get(String(line.product_id)) || 0) + line.quantity);
      if (line.selling_plan_allocation) subscribed.add(String(line.product_id));
    });
    const updates = {};
    const giftCodes = [];
    const newGifts = [];
    // tier gifts: the gift table of each product (from its first main line that has one)
    const tables = new Map();
    lines.forEach((line) => {
      if (line.properties?._gift || !line.properties?._gifts || tables.has(String(line.product_id))) return;
      try { tables.set(String(line.product_id), JSON.parse(line.properties._gifts)); } catch (error) { /* not ours */ }
    });
    let levels = {};
    try { levels = JSON.parse(store.get(giftKey) || '{}') || {}; } catch (error) { levels = {}; }
    tables.forEach((table, product) => {
      const quantity = owned.get(product) || 0;
      const tier = (table.t || []).filter((item) => item.q <= quantity).sort((a, b) => b.q - a.q)[0];
      const unlocked = (table.p || []).filter((entry) => entry[0] <= quantity);
      const level = `${tier?.q || 0}:${unlocked.map((entry) => entry[0]).join('-')}`;
      if (levels[product] === level) return;
      levels[product] = level;
      // gift lines are not the bundle: they never carry its Bundle property
      const props = (unlock) => ({ _gift: '1', _for: product, ...(unlock > 1 ? { _unlock: String(unlock) } : {}), _tg: '1' });
      const want = [
        ...(tier?.g || []).map(([id, count, code]) => ({ id: Number(id), quantity: count < 0 ? tier.q : count, unlock: tier.q, code })),
        ...unlocked.map(([unlock, id, count, code]) => ({ id: Number(id), quantity: count, unlock, code })),
      ];
      lines.filter((line) => line.properties?._gift && line.properties._tg && String(line.properties._for) === product).forEach((line) => {
        const match = want.findIndex((entry) => entry.id === line.variant_id && entry.quantity === line.quantity && String(entry.unlock > 1 ? entry.unlock : '') === String(line.properties._unlock || ''));
        if (match > -1) want.splice(match, 1);
        else updates[line.key] = 0;
      });
      want.forEach((entry) => {
        newGifts.push({ id: entry.id, quantity: entry.quantity, properties: props(entry.unlock) });
        if (entry.code) giftCodes.push(entry.code);
      });
    });
    if (tables.size) store.set(giftKey, JSON.stringify(levels));
    lines.forEach((line) => {
      const product = line.properties?._for;
      if (!line.properties?._gift || !product || line.key in updates) return;
      // tier gifts of a product that still has its gift table were handled above
      if (line.properties._tg && tables.has(String(product))) return;
      const short = (owned.get(String(product)) || 0) < (Number(line.properties._unlock) || 1);
      if (short || (line.properties._sub && !subscribed.has(String(product)))) updates[line.key] = 0;
    });

    // tier codes
    const isTier = new Set(known.map((code) => code.toLowerCase()));
    const products = new Map();
    lines.forEach((line) => {
      const table = line.properties?._tiers;
      if (!table || line.properties?._gift) return;
      const entry = products.get(line.product_id) || { quantity: 0, tiers: table.split(',').map((pair) => ({ quantity: Number(pair.split(':')[0]) || 1, code: pair.split(':').slice(1).join(':') })) };
      entry.quantity += line.quantity;
      entry.tiers.forEach((tier) => isTier.add(tier.code.toLowerCase()));
      products.set(line.product_id, entry);
    });
    const best = [...products.values()].map(({ quantity, tiers }) => tiers.filter((tier) => tier.code && tier.quantity <= quantity).sort((a, b) => b.quantity - a.quantity)[0]?.code).filter(Boolean);
    const current = (cart.discount_codes || []).map((entry) => entry.code);
    const keep = (code) => !isTier.has(code.toLowerCase());
    const next = [...new Set([...current.filter(keep), ...add.filter(keep), ...giftCodes.filter(keep), ...best])];

    const body = {};
    if (Object.keys(updates).length) body.updates = updates;
    if (next.join(',') !== current.join(',')) body.discount = next.join(',');
    if (!Object.keys(body).length && !newGifts.length) return false;
    if (Object.keys(body).length) await cartPost('cart/update.js', body);
    // New tier gifts in one request; a gift that cannot be added (sold out) does not stop the others.
    if (newGifts.length) {
      await cartPost('cart/add.js', { items: newGifts }).catch(async () => {
        for (const item of newGifts) await cartPost('cart/add.js', { items: [item] }).catch(() => {});
      });
    }
    return true;
  }

  // The cart icon opens the drawer.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest?.('[data-cart-open]');
    const cartDrawer = drawer();
    if (!opener || !cartDrawer?.active) return;
    event.preventDefault();
    cartDrawer.open();
  });

  // Add to cart anywhere on the page: add through the Ajax API, then show the drawer. "Buy now" is left alone.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const cartDrawer = drawer();
    if (!cartDrawer?.active || event.defaultPrevented || form.closest('cart-drawer')) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    if (form.querySelector('[name="return_to"][value*="checkout"]')) return;
    event.preventDefault();
    const submit = event.submitter || form.querySelector('[type="submit"], [data-submit-form]') || (form.id && document.querySelector(`[data-submit-form][form="${CSS.escape(form.id)}"]`));
    submit?.setAttribute('aria-busy', 'true');
    const data = new FormData(form);
    Object.entries(cartDrawer.sectionParams || {}).forEach(([key, value]) => data.set(key, value));
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Theme-Cart': '1' }, body: data });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message);
      cartDrawer.take(result);
    } catch (error) {
      cartDrawer.toast(error.message);
    }
    await cartDrawer.run(() => Promise.resolve());
    submit?.removeAttribute('aria-busy');
    if (cartDrawer.dataset.openAfterAdd === 'true') cartDrawer.open();
    else document.dispatchEvent(new CustomEvent('cart:added'));
  });

  // Theme editor: open while the drawer or one of its blocks is selected.
  const isDrawerSection = (event) => drawer() && event.detail?.sectionId === drawer().dataset.sectionId;
  document.addEventListener('shopify:section:select', (event) => { if (isDrawerSection(event)) drawer().open(); });
  document.addEventListener('shopify:block:select', (event) => { if (isDrawerSection(event)) drawer().open(); });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (isDrawerSection(event) && drawer().dataset.keepOpenEditor !== 'true') drawer().close();
  });
  // Upsells / recommendations with a different mobile layout (snippets/upsells.liquid): swap data-layout per screen size,
  // also for blocks rendered later (drawer re-render, recommendations, editor).
  const layouts = () => document.querySelectorAll('.cd-upsells[data-layout-mobile]').forEach((box) => {
    const layout = mobile.matches ? box.dataset.layoutMobile : box.dataset.layoutDesktop;
    if (box.dataset.layout !== layout) box.dataset.layout = layout;
  });
  mobile.addEventListener('change', layouts);
  // New recommendation boxes anywhere: check at most once per frame, and only when elements were added.
  let layoutsQueued = false;
  new MutationObserver((records) => {
    if (layoutsQueued || !records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1))) return;
    layoutsQueued = true;
    requestAnimationFrame(() => { layoutsQueued = false; layouts(); });
  }).observe(document.body, { childList: true, subtree: true });
  layouts();

  document.addEventListener('shopify:section:load', (event) => {
    if (isDrawerSection(event) && drawer().dataset.keepOpenEditor === 'true') drawer().open();
  });

  // ---------- Shopify apps ----------
  // A public handle for apps and other scripts.
  window.theme = window.theme || {};
  window.theme.cart = {
    open: () => drawer()?.open(),
    close: () => drawer()?.close(),
    refresh: () => drawer()?.reload(),
    post: cartPost,
    sync: syncCart,
  };

  // Cart changes made outside the theme (apps): refresh the drawer and the count once the request is done. A cart
  // add also opens the drawer when "Open the drawer after adding" is on.
  const cartPath = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  let pending;
  const external = (added) => {
    const cartDrawer = drawer();
    if (!cartDrawer) return;
    clearTimeout(pending);
    pending = setTimeout(async () => {
      if (Date.now() - (cartDrawer.lastRefresh || 0) > 400) await cartDrawer.reload();
      if (added && cartDrawer.active && cartDrawer.dataset.openAfterAdd === 'true') cartDrawer.open();
    }, 250);
  };
  const isOurs = (headers) => {
    if (!headers) return false;
    if (headers instanceof Headers) return headers.get('X-Theme-Cart') === '1';
    return Object.entries(headers).some(([key, value]) => key.toLowerCase() === 'x-theme-cart' && String(value) === '1');
  };
  const nativeFetch = window.fetch;
  window.fetch = function themeFetch(input, init) {
    const request = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      const match = cartPath.exec(new URL(url, window.location.origin).pathname);
      const ours = isOurs(init?.headers) || (input instanceof Request && isOurs(input.headers));
      if (match && !ours) request.then((response) => { if (response.ok) external(match[1] === 'add'); }).catch(() => {});
    } catch (error) { /* not a cart request */ }
    return request;
  };
  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function themeOpen(method, url) {
    this.themeCartUrl = String(url || '');
    return open.apply(this, arguments);
  };
  const send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function themeSend() {
    try {
      const match = cartPath.exec(new URL(this.themeCartUrl, window.location.origin).pathname);
      if (match) this.addEventListener('load', () => { if (this.status >= 200 && this.status < 300) external(match[1] === 'add'); });
    } catch (error) { /* not a cart request */ }
    return send.apply(this, arguments);
  };
  // Events apps commonly send to ask the theme for a fresh cart.
  ['cart:refresh', 'theme:cart:refresh', 'cart:build'].forEach((name) => document.addEventListener(name, () => drawer()?.reload()));
})();
